import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogTitle } from './ui/Dialog';
import useSWR from 'swr';
import { supabase } from '@fd/supabase/index';
import { useSession } from '../context/session/useSession';
import { usePostHog } from 'posthog-js/react';
import posthog from 'posthog-js';
import { honoPrivateClient } from '../api/honoClient';
import useMobile from '@fd/frontend/hooks/useMobile';

const SURVEY_ID = 'onboarding_survey';

function useOnboardingSurvey() {
  const client = usePostHog();
  const { session } = useSession();

  const [featureEnabled, setFeatureEnabled] = useState<boolean | undefined>(
    () => client.isFeatureEnabled('onboarding-survey')
  );

  useEffect(() => {
    if (session) {
      client.setPersonPropertiesForFlags({
        created_at: session?.user.created_at,
      });
    }

    return client.onFeatureFlags(() => {
      setFeatureEnabled(client.isFeatureEnabled('onboarding-survey'));
    });
  }, [client, session]);

  if (!session) {
    return false;
  }

  return featureEnabled;
}

export default function SignUpSurveyDialog() {
  const { session } = useSession();
  const isSurveyEnabled = useOnboardingSurvey();
  const isMobile = useMobile();
  const { data, isLoading, mutate } = useSWR(
    session && isSurveyEnabled ? 'onboarding-survey' : undefined,
    async () => {
      const { data, error } = await supabase
        .from('survey')
        .select('*')
        .eq('id', SURVEY_ID);

      if (error) throw error;
      return data;
    }
  );
  const isDialogOpen =
    !!(isSurveyEnabled && session && !isLoading && !isMobile && (!data || data.length === 0));

  useEffect(() => {
    if (isDialogOpen) {
      posthog.capture('Onboarding survey viewed');
    }
  }, [isDialogOpen]);

  const utmSource = localStorage.getItem('utm_source');
  const utmCampaign = localStorage.getItem('utm_campaign');
  const utmMedium = localStorage.getItem('utm_medium');
  const utm = [utmSource, utmCampaign, utmMedium].filter(Boolean).join(',');

  const insertSurvey = async (data: string) => {
    const parsedData = JSON.parse(data);
    await honoPrivateClient.event.survey.$post({
      json: {
        surveyId: SURVEY_ID,
        data: parsedData,
      },
    });
  };

  useEffect(() => {
    const handleMessage = (e: MessageEvent) => {
      if (typeof e?.data === 'string') {
        if (e.data.includes('Tally.FormLoaded')) {
          console.log('Form loaded');
        }

        if (e.data.includes('Tally.FormSubmitted')) {
          console.log('Form submitted', e);
          if (session) {
            insertSurvey(e.data).then(() => mutate());
          }
        }
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [mutate, session]);

  return (
    <Dialog open={isDialogOpen}>
      <DialogContent
        className="sm:max-w-[425px] md:min-w-[600px] h-[80dvh] bg-white"
        close={false}
      >
        <DialogTitle hidden={true}>Sign up poll</DialogTitle>
        <iframe
          src={`https://tally.so/r/w8ENBl?transparentBackground=1&email=${session?.user.email}&user_id=${session?.user.id}&utm=${utm}`}
          width="100%"
          height="100%"
        ></iframe>
      </DialogContent>
    </Dialog>
  );
}
